/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Tooltip from 'antd/lib/tooltip';

import ContainerCard from '../../../../components/ui/ContainerCard/ContainerCard';
import SelectInput from '../../../../components/ui/Inputs/SelectInput/SelectInput';
import { notify } from '../../../../components/ui/Toast/Toast';
import Button from '../../../../components/ui/Button/Button';
import Error from '../../../../components/ui/Error/Error';
import { DealActions } from '../../../../actions';
import { getErrors } from '../../../../utils';

import deleteIconBlue from '../../../../assets/icons/blueStyle/delete-blue.png';
import deleteIcon from '../../../../assets/icons/actionsbutton/delete.png';

export default function ThirdPartyPayerPopup({
    setView,
    data,
    refreshData,
    blueStyle,
    userData,
    customers,
    handleGetCustomers
}) {
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const [thirdPartyPayers, setThirdPartyPayers] = useState([{
        id: Date.now(),
        value: null
    }]); // Un select par défaut

    const defaultValues = {
        third_party_payers: [],
    }

    const {handleSubmit, reset} = useForm({defaultValues});

    useEffect(() => {
        if (!data) return;

        const initialPayers = data.third_party_payers || [];
        // Si il y a des données existantes, les utiliser
        if (initialPayers.length > 0) {
            setThirdPartyPayers(initialPayers.map(payer => ({
                id: payer.id,
                value: {
                    id: payer.id,
                    name: payer.name
                }
            })));
        }
        // Sinon, garder le select par défaut qui est déjà initialisé
        reset({
            third_party_payers: initialPayers.map(payer => ({
                id: payer.id,
                value: {
                    id: payer.id,
                    name: payer.name
                }
            })),
        });
    }, [data]);

    const addThirdPartyPayer = () => {
        // Vérifier qu'on n'a pas atteint la limite de 10
        if (thirdPartyPayers.length >= 10) return;

        const newPayer = {
            id: Date.now(), // ID temporaire pour la clé React
            value: null
        };
        setThirdPartyPayers([...thirdPartyPayers, newPayer]);
    };

    // Fonction pour vérifier si le dernier tiers payant est vide
    const isLastPayerEmpty = () => {
        if (thirdPartyPayers.length === 0) return true;
        const lastPayer = thirdPartyPayers[thirdPartyPayers.length - 1];
        return !lastPayer.value || lastPayer.value === null;
    };

    // Fonction pour vérifier si on peut ajouter un nouveau tiers payant
    const canAddPayer = () => {
        return !isLastPayerEmpty() && thirdPartyPayers.length < 10;
    };

    // Fonction pour vérifier si on peut supprimer un tiers payant
    const canRemovePayer = () => {
        return thirdPartyPayers.length > 0;
    };

    // Fonction pour vérifier si au moins un tiers payant est sélectionné
    const hasAtLeastOnePayer = () => {
        return thirdPartyPayers.some(payer => payer.value && payer.value !== null);
    };

    const removeThirdPartyPayer = (id) => {
        const updatedPayers = thirdPartyPayers.filter(payer => payer.id !== id);
        setThirdPartyPayers(updatedPayers);
    };

    const updateThirdPartyPayer = (id, value) => {
        const updatedPayers = thirdPartyPayers.map(payer =>
            payer.id === id ? { ...payer, value } : payer
        );
        setThirdPartyPayers(updatedPayers);
    };

    const onSubmit = async () => {
        setLoading(true);
        setDisabled(true);

        const updatedDeal = {
            third_party_payer_ids: thirdPartyPayers.map(payer => payer.value.id)
        };

        const result = await DealActions.updateDeal(userData, updatedDeal, data.id);

        if (!result.success) {
            setErrors(getErrors(result));
        }
        else {
            await refreshData();
            setView(false);
            notify('Les tiers payants ont été mis à jour');
        }

        setLoading(false);
        setDisabled(false);
    }

    return (
        <div>
            <ContainerCard
                title={'Tiers payants'}
                coloredButtonDisplayed={false}
                overflow='overflow-visible'
            >
                <div className='w-full flex flex-col gap-4'>
                    {/* Liste des tiers payants existants */}
                    {thirdPartyPayers.map((payer) => (
                        <div key={payer.id} className='flex items-center gap-2'>
                            <div className='flex-1'>
                                <SelectInput
                                    label={''}
                                    blueStyle={blueStyle}
                                    placeholder='Sélectionner un tiers payant'
                                    width='w-full'
                                    fetchFunction={handleGetCustomers}
                                    isSearchable={true}
                                    hasNextPage={customers?.hasNextPage}
                                    loading={!customers}
                                    value={payer.value}
                                    options={customers?.options}
                                    onChange={(value) => updateThirdPartyPayer(payer.id, value)}
                                    labelKeys={['name']}
                                    disabled={disabled}
                                    menuHeight='120px'
                                />
                            </div>

                            {canRemovePayer() && (
                                <button
                                    type="button"
                                    onClick={() => removeThirdPartyPayer(payer.id)}
                                    disabled={disabled}
                                    className=" bg-transparent"
                                >
                                    <Tooltip title='Supprimer'>
                                        <div>
                                            <img src={blueStyle ? deleteIconBlue : deleteIcon} alt="logo" className="w-4"/>
                                        </div>
                                    </Tooltip>
                                </button>
                            )}
                        </div>
                    ))}

                    <div className='flex justify-start'>
                        <button
                            onClick={addThirdPartyPayer}
                            className=" bg-transparent"
                            disabled={disabled || !canAddPayer()}
                        >
                            <Tooltip title={thirdPartyPayers.length >= 10 ? 'Limite de 10 tiers payants atteinte' : 'Ajouter un tiers payant'}>
                                <div
                                    className={`whitespace-nowrap cursor-pointer text-sm ${!canAddPayer() ? "text-[#b2bec3]" : "text-gradient"} ml-1`}
                                >
                                    <p>Ajouter un tiers payant</p>
                                </div>
                            </Tooltip>
                        </button>
                    </div>
                </div>

                <div className="flex flex-col justify-center pb-2 mt-2">
                    <div className="flex justify-center pb-2">
                        <Button
                            onClick={() => setView(false)}
                            type={'secondary'}
                            content={'Annuler'}
                            disabled={disabled}
                        />
                        <Button
                            type={blueStyle ? 'blue' : 'primary'}
                            content={'Sauvegarder'}
                            onClick={handleSubmit(onSubmit)}
                            loading={loading}
                            disabled={disabled || (!hasAtLeastOnePayer() && thirdPartyPayers.length > 0)}
                        />
                    </div>

                    <Error errors={errors}/>
                </div>
            </ContainerCard>
        </div>
    )
}